/**
 * Formatação pt-BR — implementação única das regras de design.md §10.
 *
 * Nenhum outro lugar do código formata moeda, data, percentual ou coordenada.
 * Formatação duplicada é o caminho mais curto para divergência entre telas.
 */

import { cnpj } from 'cpf-cnpj-validator';

import { DEFAULT_TIMEZONE } from './timezones.js';

export const APP_LOCALE = 'pt-BR';

/*
 * O fuso deixou de ser constante.
 *
 * Era `America/Cuiaba` cravado aqui, e a coluna `organizations.timezone`
 * existia sem que ninguém a lesse — um órgão de outro estado veria toda data
 * deslocada e não teria como corrigir.
 *
 * No navegador há uma sessão só, de um órgão só: o fuso é definido uma vez, ao
 * entrar, e vale para as duzentas chamadas de `formatDate` espalhadas pelas
 * telas. No servidor não pode haver padrão global — duas requisições de órgãos
 * diferentes se cruzam —, e por isso todas as funções aceitam o fuso por
 * parâmetro, que é como o PDF o recebe.
 */
let fusoPadrao: string = DEFAULT_TIMEZONE;

/** Define o fuso das telas. Chamado uma vez, quando a sessão é aplicada. */
export function setDefaultTimezone(timeZone: string): void {
  fusoPadrao = timeZone;
}

export function defaultTimezone(): string {
  return fusoPadrao;
}

/** Sinal de menos tipográfico (U+2212), não hífen. */
const MINUS = '−';

// ───────────────────────────────────────────────────────────── moeda

/**
 * Valores monetários trafegam e são persistidos em centavos inteiros.
 * Ponto flutuante para dinheiro é proibido — ver ADR 009.
 */
export type Cents = number;

const currencyFormatter = new Intl.NumberFormat(APP_LOCALE, {
  style: 'currency',
  currency: 'BRL',
});

const currencyCompactFormatter = new Intl.NumberFormat(APP_LOCALE, {
  style: 'currency',
  currency: 'BRL',
  notation: 'compact',
  maximumFractionDigits: 1,
});

/** `396_249_000` → `"R$ 3.962.490,00"` */
export function formatCurrency(cents: Cents): string {
  return currencyFormatter.format(cents / 100);
}

/** `396_249_000` → `"R$ 3,96 mi"`. Uso restrito a KPI; tabela sempre usa a forma completa. */
export function formatCurrencyCompact(cents: Cents): string {
  return currencyCompactFormatter.format(cents / 100);
}

/**
 * Variação financeira com sinal explícito: `"+R$ 192.455,77"` / `"−R$ 12.000,00"`.
 * Zero sai sem sinal.
 */
export function formatCurrencyDelta(cents: Cents): string {
  const absolute = currencyFormatter.format(Math.abs(cents) / 100);
  if (cents > 0) return `+${absolute}`;
  if (cents < 0) return `${MINUS}${absolute}`;
  return absolute;
}

/**
 * Máscara de dinheiro enquanto se digita.
 *
 * Trabalha da direita para a esquerda, tratando tudo o que foi digitado como
 * centavos: `1` vira `R$ 0,01`, `123` vira `R$ 1,23`, `1234567` vira
 * `R$ 12.345,67`. É o comportamento de caixa eletrônico, e é o que evita o erro
 * caro deste sistema — digitar `250000` querendo duzentos e cinquenta mil e
 * gravar dois mil e quinhentos, ou o contrário.
 *
 * Formatar só ao sair do campo, como era antes, deixa o valor ambíguo
 * justamente enquanto a pessoa o confere.
 */
export function maskCurrency(input: string): string {
  const digitos = input.replace(/\D/g, '').slice(0, 15);
  if (digitos === '') return '';

  return formatCurrency(Number(digitos));
}

/**
 * Máscara de telefone, fixo ou celular.
 *
 * O tamanho decide o formato: dez dígitos são `(65) 3831-1200`, onze são
 * `(65) 98431-1200`. A conta da BrasilAPI vem colada — `"6740381216"` —, e sem
 * isto o número entrava no cadastro do mesmo jeito.
 */
export function maskPhone(input: string): string {
  const d = input.replace(/\D/g, '').slice(0, 11);
  if (d.length === 0) return '';
  if (d.length <= 2) return `(${d}`;

  const ddd = d.slice(0, 2);
  const resto = d.slice(2);
  const corte = resto.length > 8 ? 5 : 4;

  return resto.length <= corte
    ? `(${ddd}) ${resto}`
    : `(${ddd}) ${resto.slice(0, corte)}-${resto.slice(corte)}`;
}

/** Converte entrada do usuário (`"3.972.490,00"`, `"3972490"`) em centavos. */
export function parseCurrencyToCents(input: string): Cents | null {
  const cleaned = input.replace(/[^\d,.-]/g, '').trim();
  if (cleaned === '') return null;

  // pt-BR: ponto é separador de milhar, vírgula é decimal.
  const normalized = cleaned.replace(/\./g, '').replace(',', '.');
  const value = Number(normalized);
  if (!Number.isFinite(value)) return null;

  return Math.round(value * 100);
}

// ────────────────────────────────────────────────────────────── datas

/** `"2026-08-03"` — data sem hora, que não deve sofrer conversão de fuso. */
const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

export type DateInput = string | number | Date;

function toDate(input: DateInput): Date | null {
  if (input instanceof Date) return Number.isNaN(input.getTime()) ? null : input;

  if (typeof input === 'string' && DATE_ONLY.test(input)) {
    // Ancorado em UTC e formatado em UTC: uma data-calendário nunca "anda" de dia.
    const date = new Date(`${input}T00:00:00.000Z`);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  const date = new Date(input);
  return Number.isNaN(date.getTime()) ? null : date;
}

function isDateOnly(input: DateInput): boolean {
  return typeof input === 'string' && DATE_ONLY.test(input);
}

/**
 * Fuso inválido não derruba a tela.
 *
 * `Intl` lança para qualquer identificador que não reconheça, inclusive a
 * string vazia — e como formatação de data aparece em toda página, uma exceção
 * aqui apaga o sistema inteiro. Já aconteceu: a tela de configurações quebrou
 * no primeiro quadro, antes de o valor chegar do servidor. O que cabe a esta
 * função é escrever uma data, não decidir o destino da aplicação.
 */
function dateFormatter(
  options: Intl.DateTimeFormatOptions,
  utc: boolean,
  timeZone?: string,
): Intl.DateTimeFormat {
  const escolhido = utc ? 'UTC' : (timeZone ?? fusoPadrao);

  try {
    return new Intl.DateTimeFormat(APP_LOCALE, { ...options, timeZone: escolhido });
  } catch {
    return new Intl.DateTimeFormat(APP_LOCALE, { ...options, timeZone: DEFAULT_TIMEZONE });
  }
}

/** `"03/08/2026"` */
export function formatDate(input: DateInput | null | undefined, timeZone?: string): string {
  if (input == null) return '—';
  const date = toDate(input);
  if (!date) return '—';

  return dateFormatter(
    { day: '2-digit', month: '2-digit', year: 'numeric' },
    isDateOnly(input),
    timeZone,
  ).format(date);
}

/** `"03/08/2026 17:22"` */
export function formatDateTime(input: DateInput | null | undefined, timeZone?: string): string {
  if (input == null) return '—';
  const date = toDate(input);
  if (!date) return '—';

  return dateFormatter(
    {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    },
    false,
    timeZone,
  )
    .format(date)
    .replace(',', '');
}

/** `"3 de ago de 2026"` */
export function formatDateLong(input: DateInput | null | undefined): string {
  if (input == null) return '—';
  const date = toDate(input);
  if (!date) return '—';

  return dateFormatter(
    { day: 'numeric', month: 'short', year: 'numeric' },
    isDateOnly(input),
  ).format(date);
}

/** Diferença em dias-calendário entre duas datas. Positivo = `b` no futuro. */
/**
 * A data de hoje no fuso informado, em `yyyy-mm-dd`.
 *
 * Os serviços faziam `new Date().toISOString().slice(0, 10)`, que é a data em
 * UTC. Em Cuiabá, das 20h à meia-noite o servidor já achava que era o dia
 * seguinte — e uma obra passava a constar como "visita vencida" quatro horas
 * antes de vencer de verdade. O mesmo cálculo em Rio Branco erra cinco horas.
 *
 * `en-CA` porque o formato dele é justamente `aaaa-mm-dd`; montar a string à
 * mão a partir das partes daria o mesmo resultado com o dobro de linhas.
 */
export function todayIn(timeZone: string): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());
}

export function daysBetween(a: DateInput, b: DateInput): number {
  const from = toDate(a);
  const to = toDate(b);
  if (!from || !to) return 0;

  const MS_PER_DAY = 86_400_000;
  const fromUtc = Date.UTC(from.getUTCFullYear(), from.getUTCMonth(), from.getUTCDate());
  const toUtc = Date.UTC(to.getUTCFullYear(), to.getUTCMonth(), to.getUTCDate());

  return Math.round((toUtc - fromUtc) / MS_PER_DAY);
}

/** `360` → `"360 dias"`; `1` → `"1 dia"` */
export function formatDays(days: number): string {
  return `${days.toLocaleString(APP_LOCALE)} ${Math.abs(days) === 1 ? 'dia' : 'dias'}`;
}

/**
 * Distância no tempo em linguagem corrente: `"hoje"`, `"há 3 dias"`,
 * `"há 2 meses"`. Serve a "quando foi a última movimentação desta obra", onde
 * a data exata importa menos que a ordem de grandeza.
 *
 * `reference` existe para o teste poder fixar o presente.
 */
export function formatRelativeDate(
  input: DateInput | null | undefined,
  reference: DateInput = new Date(),
): string {
  if (input === null || input === undefined || input === '') return '—';

  const days = daysBetween(input, reference);

  if (days < 0) return formatDate(input);
  if (days === 0) return 'hoje';
  if (days === 1) return 'ontem';
  if (days < 30) return `há ${days} dias`;

  const months = Math.floor(days / 30);
  if (months < 12) return `há ${months} ${months === 1 ? 'mês' : 'meses'}`;

  const years = Math.floor(days / 365);
  return `há ${years} ${years === 1 ? 'ano' : 'anos'}`;
}

// ───────────────────────────────────────────────────────── percentual

/** `0.94` → `"94,0%"`. Recebe fração, não valor já multiplicado. */
export function formatPercent(fraction: number, fractionDigits = 1): string {
  if (!Number.isFinite(fraction)) return '—';

  return new Intl.NumberFormat(APP_LOCALE, {
    style: 'percent',
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(fraction);
}

/** Razão protegida contra divisão por zero, limitada a [0, 1]. */
export function ratio(part: number, total: number): number {
  if (!total) return 0;
  return Math.min(Math.max(part / total, 0), 1);
}

// ───────────────────────────────────────────────────── dados técnicos

/** `-10.178211` (latitude) → `"10°10'41,560\"S"` */
export function formatCoordinate(value: number, axis: 'lat' | 'lng'): string {
  if (!Number.isFinite(value)) return '—';

  const hemisphere = axis === 'lat' ? (value >= 0 ? 'N' : 'S') : value >= 0 ? 'L' : 'O';

  const absolute = Math.abs(value);
  const degrees = Math.floor(absolute);
  const minutesFloat = (absolute - degrees) * 60;
  const minutes = Math.floor(minutesFloat);
  const seconds = (minutesFloat - minutes) * 60;

  const secondsText = seconds.toFixed(3).replace('.', ',');
  return `${degrees}°${String(minutes).padStart(2, '0')}'${secondsText}"${hemisphere}`;
}

/** `"12345678000190"` → `"12.345.678/0001-90"` */
export function formatCnpj(value: string): string {
  /*
   * A máscara vem do pacote, e não de um `replace` com regex de dígitos: o CNPJ
   * alfanumérico (julho de 2026) tem letra nos oito primeiros blocos, e a regex
   * antiga devolvia o valor cru sempre que encontrava uma.
   */
  return cnpj.format(value);
}

/** `"11987654321"` → `"(11) 98765-4321"` */
export function formatPhone(value: string): string {
  const digits = value.replace(/\D/g, '');
  if (digits.length === 11) return digits.replace(/^(\d{2})(\d{5})(\d{4})$/, '($1) $2-$3');
  if (digits.length === 10) return digits.replace(/^(\d{2})(\d{4})(\d{4})$/, '($1) $2-$3');
  return value;
}

/** `1_048_576` → `"1 MB"` */
export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;

  const units = ['KB', 'MB', 'GB'] as const;
  let value = bytes / 1024;
  let unitIndex = 0;

  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }

  const unit = units[unitIndex] ?? 'GB';
  return `${value.toLocaleString(APP_LOCALE, { maximumFractionDigits: 1 })} ${unit}`;
}

/** Iniciais para avatar: `"Tulio Augusto Pereira Silva"` → `"TS"` */
export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';

  const first = parts[0]?.[0] ?? '';
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : '';

  return (first + last).toUpperCase();
}

/**
 * Máscara de CNPJ aplicada enquanto se digita.
 *
 * Diferente de {@link formatCnpj}, que só formata o documento inteiro: esta
 * acompanha o campo a cada tecla e devolve o que já dá para pontuar.
 *
 * Os oito primeiros blocos aceitam letra — é o formato que vale a partir de
 * julho de 2026 —, e só os dois dígitos verificadores continuam numéricos. Um
 * campo que recusasse letra impediria de cadastrar um CNPJ legítimo.
 */
export function maskCnpj(input: string): string {
  const cru = input
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '')
    .slice(0, 14);

  const raiz = cru.slice(0, 12);
  const verificadores = cru.slice(12).replace(/\D/g, '');

  const partes = [raiz.slice(0, 2), raiz.slice(2, 5), raiz.slice(5, 8), raiz.slice(8, 12)].filter(
    (parte) => parte !== '',
  );

  let texto = partes[0] ?? '';
  if (partes[1]) texto += `.${partes[1]}`;
  if (partes[2]) texto += `.${partes[2]}`;
  if (partes[3]) texto += `/${partes[3]}`;
  if (verificadores) texto += `-${verificadores}`;

  return texto;
}
